import React from 'react';

// Deterministic label colors so a category always renders the same hue.
const LABEL_STYLES = [
  'bg-sky-500/15 text-sky-300',
  'bg-emerald-500/15 text-emerald-300',
  'bg-amber-500/15 text-amber-300',
  'bg-rose-500/15 text-rose-300',
  'bg-violet-500/15 text-violet-300',
  'bg-teal-500/15 text-teal-300',
  'bg-orange-500/15 text-orange-300',
  'bg-blue-500/15 text-blue-300',
];

export function labelStyle(category) {
  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    hash = (hash * 31 + category.charCodeAt(i)) >>> 0;
  }
  return LABEL_STYLES[hash % LABEL_STYLES.length];
}

export function formatShortDate(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function CaretUpIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
      <path d="M5 2.5L8.5 6.5H1.5L5 2.5Z" fill="currentColor" />
    </svg>
  );
}

function CaretDownIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
      <path d="M5 7.5L1.5 3.5H8.5L5 7.5Z" fill="currentColor" />
    </svg>
  );
}

export default function KanbanCard({
  item,
  isEditable,
  onClick,
  canMoveToTop = false,
  canMoveToBottom = false,
  onMoveToTop,
  onMoveToBottom,
  onMoveToInProgress,
  onMoveToDone
}) {
  const handleDragStart = (e) => {
    if (!isEditable) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData('text/plain', item.id);
    e.dataTransfer.setData('application/json', JSON.stringify({ id: item.id }));
    e.dataTransfer.effectAllowed = 'move';
    // Dim the source card after the browser captures the drag image
    const el = e.currentTarget;
    requestAnimationFrame(() => el.classList.add('opacity-40'));
  };

  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove('opacity-40');
  };

  const stopCardAction = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const showMoveControls = isEditable && (canMoveToTop || canMoveToBottom);
  const canSendToInProgress = isEditable && item.column !== 'In Progress' && !!onMoveToInProgress;
  const canSendToDone = isEditable && item.column !== 'Done' && !!onMoveToDone;
  const showStatusButtons = canSendToInProgress || canSendToDone;

  return (
    <div
      data-card-id={item.id}
      draggable={isEditable}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={() => onClick(item)}
      className="group relative rounded-lg bg-neutral-800 border border-neutral-700/50 px-3 py-2 cursor-pointer hover:border-neutral-500 transition-colors select-none"
    >
      {showMoveControls && (
        <div
          className="absolute top-1 right-1 flex flex-col opacity-100 md:opacity-0 md:group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
          onClick={stopCardAction}
          onMouseDown={stopCardAction}
        >
          <button
            type="button"
            disabled={!canMoveToTop}
            onClick={(e) => {
              stopCardAction(e);
              if (canMoveToTop) onMoveToTop?.();
            }}
            className="p-1 md:p-0.5 rounded text-neutral-400 hover:text-neutral-100 hover:bg-neutral-700/80 disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-neutral-400 disabled:cursor-default touch-manipulation"
            title="Move to top"
            aria-label="Move to top"
          >
            <CaretUpIcon />
          </button>
          <button
            type="button"
            disabled={!canMoveToBottom}
            onClick={(e) => {
              stopCardAction(e);
              if (canMoveToBottom) onMoveToBottom?.();
            }}
            className="p-1 md:p-0.5 rounded text-neutral-400 hover:text-neutral-100 hover:bg-neutral-700/80 disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-neutral-400 disabled:cursor-default touch-manipulation"
            title="Move to bottom"
            aria-label="Move to bottom"
          >
            <CaretDownIcon />
          </button>
        </div>
      )}
      {item.category && (
        <span className={`inline-block px-1.5 py-px rounded text-[10px] font-medium mb-1 ${labelStyle(item.category)}`}>
          {item.category}
        </span>
      )}
      <div className={`text-sm text-neutral-100 leading-snug break-words ${showMoveControls ? 'pr-5' : ''}`}>
        {item.title}
      </div>
      {(item.description || item.finishedAt) && (
        <div className="flex items-center gap-2 mt-1.5 text-[11px] text-neutral-500">
          {item.description && (
            <span title="This card has a description" aria-label="Has description">≡</span>
          )}
          {item.finishedAt && (
            <span className="text-emerald-400/80">✓ {formatShortDate(item.finishedAt)}</span>
          )}
        </div>
      )}
      {showStatusButtons && (
        <div
          className="flex flex-wrap items-center gap-1.5 mt-2"
          onClick={stopCardAction}
          onMouseDown={stopCardAction}
        >
          {canSendToInProgress && (
            <button
              type="button"
              onClick={(e) => {
                stopCardAction(e);
                onMoveToInProgress?.();
              }}
              className="px-2 py-1 md:px-1.5 md:py-0.5 rounded text-[11px] md:text-[10px] font-medium text-sky-300/90 bg-sky-500/10 hover:bg-sky-500/20 hover:text-sky-200 transition-colors touch-manipulation"
              title="Move to In Progress"
              aria-label="Move to In Progress"
            >
              In Progress
            </button>
          )}
          {canSendToDone && (
            <button
              type="button"
              onClick={(e) => {
                stopCardAction(e);
                onMoveToDone?.();
              }}
              className="px-2 py-1 md:px-1.5 md:py-0.5 rounded text-[11px] md:text-[10px] font-medium text-emerald-300/90 bg-emerald-500/10 hover:bg-emerald-500/20 hover:text-emerald-200 transition-colors touch-manipulation"
              title="Move to Done"
              aria-label="Move to Done"
            >
              Done
            </button>
          )}
        </div>
      )}
    </div>
  );
}
